import { resolveBrandColor } from '../../Map/utils/sharedBranding';
import { colors } from '../../styles/theme';
import { CAD_LIST_PIN_IMAGE_ID, CAD_LIST_PIN_STYLE } from '../../Map/styles/mapTheme';

/** Material Outlined `map` 24×24: loteo en el territorio, no el pin de lote. */
const MAP_ICON_PATH = 'M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM9 16.89l-4 1.33V7.11l4-1.4zm6 1.4-4-1.4V5.71l4 1.4z';

/**
 * Color del badge. Marca personalized → `primary_color`; si no, Terrio (`infoBlue`).
 *
 * @param {{ primary_color?: string }|null|undefined} brand
 * @returns {string}
 */
export function resolveCadListPinFillColor(brand) {
  return resolveBrandColor(brand?.primary_color, CAD_LIST_PIN_STYLE.fillColor);
}

/**
 * @param {string} fillColor
 * @returns {string}
 */
export function cadListPinImageId(fillColor) {
  const color = resolveBrandColor(fillColor, CAD_LIST_PIN_STYLE.fillColor).toLowerCase();
  if (color === String(CAD_LIST_PIN_STYLE.fillColor).toLowerCase()) {
    return CAD_LIST_PIN_IMAGE_ID;
  }
  return `${CAD_LIST_PIN_IMAGE_ID}-${color.replace('#', '')}`;
}

function renderCadListPinImageData(fillColor) {
  const { iconPixelSize: size, iconPixelRatio } = CAD_LIST_PIN_STYLE;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 3;

  ctx.clearRect(0, 0, size, size);
  ctx.shadowColor = 'rgba(0,0,0,0.28)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 1;

  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fillStyle = resolveBrandColor(fillColor, CAD_LIST_PIN_STYLE.fillColor);
  ctx.fill();

  ctx.shadowColor = 'transparent';
  ctx.lineWidth = 3;
  ctx.strokeStyle = CAD_LIST_PIN_STYLE.strokeColor;
  ctx.stroke();

  const iconSize = size * 0.48;
  ctx.save();
  ctx.translate(cx - iconSize / 2, cy - iconSize / 2);
  ctx.scale(iconSize / 24, iconSize / 24);
  ctx.fillStyle = colors.backgroundLight;
  ctx.fill(new Path2D(MAP_ICON_PATH));
  ctx.restore();

  return {
    data: ctx.getImageData(0, 0, size, size),
    pixelRatio: iconPixelRatio,
  };
}

/**
 * Badge de lista CAD. Receta de puntos de interés; color Terrio o de marca.
 *
 * @param {object} map
 * @param {string} [fillColor]
 * @returns {boolean}
 */
export function ensureCadListPinImage(map, fillColor = CAD_LIST_PIN_STYLE.fillColor) {
  if (!map || typeof map.addImage !== 'function' || typeof document === 'undefined') {
    return false;
  }
  const imageId = cadListPinImageId(fillColor);
  try {
    if (map.hasImage(imageId)) return true;
  } catch {
    return false;
  }
  const rendered = renderCadListPinImageData(fillColor);
  if (!rendered) return false;
  try {
    map.addImage(imageId, rendered.data, { pixelRatio: rendered.pixelRatio });
    return true;
  } catch {
    return false;
  }
}

/**
 * @param {object} map
 * @param {object[]} pins
 * @returns {boolean}
 */
export function ensureCadListPinImages(map, pins) {
  const colorsNeeded = new Set(
    (Array.isArray(pins) ? pins : []).map((pin) => (
      pin?.fillColor || CAD_LIST_PIN_STYLE.fillColor
    )),
  );
  if (colorsNeeded.size === 0) colorsNeeded.add(CAD_LIST_PIN_STYLE.fillColor);
  let ok = true;
  colorsNeeded.forEach((color) => {
    if (!ensureCadListPinImage(map, color)) ok = false;
  });
  return ok;
}

/**
 * @param {object} map
 * @param {string} imageId
 * @returns {boolean}
 */
export function ensureCadListPinImageForId(map, imageId) {
  const id = String(imageId || '');
  if (!id.startsWith(CAD_LIST_PIN_IMAGE_ID)) return false;
  if (id === CAD_LIST_PIN_IMAGE_ID) return ensureCadListPinImage(map);
  const hex = id.slice(CAD_LIST_PIN_IMAGE_ID.length + 1);
  if (!/^[0-9a-f]{3,8}$/i.test(hex)) return ensureCadListPinImage(map);
  return ensureCadListPinImage(map, `#${hex}`);
}
